/**
 * collectAnnotationRects.ts — SW-8943
 *
 * Walks the editor container (or any ParentNode) for `.annotation` spans and
 * returns their bounding rects in the window's viewport coordinate space.
 *
 * The editor lives in the main document, so `getBoundingClientRect()` values
 * are already viewport coordinates; `offsetTop` exists only for callers that
 * still need to translate from another coordinate space (pass 0 otherwise).
 *
 * A single span's `data-annotation-id` may be a space-separated list
 * of ids (two comments sharing a word). We return all of them so the
 * caller can pick the first one that resolves to a known comment.
 */
import type { AnnotationRect } from "./pickMidlineAnnotation";
import { parseAnnotationIds } from "./pickMidlineAnnotation";

export function collectAnnotationRects(
  root: ParentNode | null,
  offsetTop: number,
  activeAnnotationIds?: Iterable<string>
): AnnotationRect[] {
  if (!root) return [];

  const activeIdSet = activeAnnotationIds
    ? new Set(Array.from(activeAnnotationIds).map((id) => String(id).trim()).filter(Boolean))
    : null;
  if (activeIdSet && activeIdSet.size === 0) return [];

  const spans = root.querySelectorAll(".annotation");
  const rects: AnnotationRect[] = [];

  spans.forEach((span) => {
    const raw = (span as Element).getAttribute("data-annotation-id");
    const annotationIds = parseAnnotationIds(raw);
    const matchingAnnotationIds = activeIdSet
      ? annotationIds.filter((id) => activeIdSet.has(id))
      : annotationIds;
    if (activeIdSet && matchingAnnotationIds.length === 0) return;

    const rect = (span as Element).getBoundingClientRect();
    rects.push({
      annotationIds: matchingAnnotationIds,
      top: offsetTop + rect.top,
      bottom: offsetTop + rect.bottom,
    });
  });

  return rects;
}
